import { type ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { BackButton } from './BackButton';

/**
 * Sdílený modal — Plex glass panel + vignette overlay (LHUI).
 *
 * Vždy portál do document.body: widget karty používají backdrop-filter
 * (acm-glass), což dělá z `position: fixed` containing block — modal by
 * jinak vyplnil jen kartu (prázdné „velké okno" místo logů/configu).
 *
 * Použití:
 *   <Modal open={showEdit} onClose={() => setShowEdit(false)} title="Upravit hostitele">
 *     <form>...</form>
 *   </Modal>
 *
 * Props:
 *   open      — true = zobrazit
 *   onClose   — zavřít (klik na overlay nebo X)
 *   title     — záhlaví modalu (vynech pro modal bez hlavičky)
 *   size      — sm (400px) | md (512px) | lg (672px) | xl (896px) | full
 *   footer    — slot pro tlačítka v patičce
 *   closeBtn  — zobrazit × tlačítko (default true)
 *   onBack    — drill-down: šipka zpět v záhlaví (detail položky → rodič)
 *   backLabel — popisek zpět (default i18n common.back)
 */

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

const MAX_W: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-full mx-4',
};

interface ModalProps {
  open?: boolean;
  /** Alias — starší obrazovky posílaly isOpen. */
  isOpen?: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: ModalSize;
  closeBtn?: boolean;
  /** Návrat na rodičovskou obrazovku (nastavení → položka). Overlay/Escape taky. */
  onBack?: () => void;
  backLabel?: ReactNode;
  className?: string;
}

export function Modal({
  open,
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  closeBtn = true,
  onBack,
  backLabel,
  className,
}: ModalProps) {
  const leave = onBack ?? onClose;
  const shown = open ?? isOpen ?? false;
  useEffect(() => {
    if (!shown) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') leave();
    };
    window.addEventListener('keydown', handler);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = prev;
    };
  }, [shown, leave]);

  if (!shown) return null;

  const node = (
    <div
      className='acm-overlay fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto p-4 sm:p-6'
      onClick={leave}
      role='presentation'
    >
      <div
        role='dialog'
        aria-modal='true'
        className={cn(
          /* rounded-2xl — o stupeň plnější než Button/Select (lg), stále sklo */
          'w-full rounded-2xl acm-glass-strong animate-fade-in my-auto',
          'max-h-[min(90vh,calc(100dvh-2rem))] flex flex-col min-h-0',
          'border border-gray-800/40',
          MAX_W[size],
          className,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {(title || closeBtn || onBack) && (
          <div className='flex items-center justify-between gap-3 border-b border-gray-800/50 px-5 py-3.5 shrink-0'>
            {onBack
              ? (
                <BackButton
                  onClick={onBack}
                  label={backLabel}
                  crumb={title}
                  className='min-w-0'
                />
              )
              : title
              ? (
                <h3 className='text-base font-semibold text-gray-100 tracking-tight truncate min-w-0'>
                  {title}
                </h3>
              )
              : <span />}
            {closeBtn && (
              <button
                type='button'
                onClick={onClose}
                className='rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-800/60 hover:text-gray-100 shrink-0'
              >
                <X size={16} />
              </button>
            )}
          </div>
        )}

        <div className='p-5 overflow-y-auto min-h-0 flex-1'>
          {children}
        </div>

        {footer && (
          <div className='flex items-center justify-end gap-2 border-t border-gray-800/50 px-5 py-4 bg-gray-950/20 shrink-0'>
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(node, document.body);
}
